import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getRing } from "../api/products";
import { requestGeneration, type GenerationRequest } from "../api/ringswap";
import { useAuth } from "../auth/AuthProvider";
import type { AuthService } from "../auth/auth-service";
import { BatchGrid, type BatchItem } from "../bench/BatchGrid";
import { BeforeAfter } from "../bench/BeforeAfter";
import { Catalogue } from "../bench/Catalogue";
import { toCurl } from "../bench/curl";
import { isRecorded, KNOWN_METALS, metalDisplay } from "../bench/enums";
import { emptySift, emptyWire, type SiftFilters, type SpecimenKind, type WireFilters } from "../bench/filters";
import { colourBranch, toDisplayUrl, type GemstoneSummary, type RingDetail } from "../bench/mapping";
import { PayloadPreview } from "../bench/PayloadPreview";
import { POLL_MAX_TRIES, POLL_WINDOW_MS, pollToFinal } from "../bench/poll";
import { config } from "../config";
import type { BenchPrefill } from "../history/prefill";

const GENERATIONS_URL = `${config.apiBase}${config.ringSwapPrefix}/v1/generations`;

/** Join truthy class names — keeps selection-state markup readable. */
const cx = (...c: Array<string | false | null | undefined>): string => c.filter(Boolean).join(" ");

/** "6m" / "90s" — how long the bench watches before it stops, said in the UI's own words. */
const pollWindowLabel = (): string => {
  const secs = Math.round(POLL_WINDOW_MS / 1000);
  return secs % 60 === 0 ? `${secs / 60}m` : `${secs}s`;
};

// ── generate → poll orchestration (the id-based flow; the API registers/resolves implicitly) ─────
/** `stopped` is not a failure — the bench gave up watching a job that is still running. */
type StepStatus = "running" | "done" | "error" | "stopped";
interface Step {
  label: string;
  status: StepStatus;
  detail?: string;
}
interface RunState {
  running: boolean;
  steps: Step[];
  result?: { status: string; url?: string | undefined };
  batch?: BatchItem[];
  error?: string;
}

/** Genuinely bad end states. `timeout` is deliberately NOT here: it means the bench stopped
 *  watching after POLL_WINDOW_MS, not that the job died — it is still running and will still cache
 *  and land in History. Calling that an error sends a tester to debug a job that is fine. */
const TERMINAL_BAD = new Set(["failed", "rejected"]);

function useBenchRun(service: AuthService) {
  const [state, setState] = useState<RunState>({ running: false, steps: [] });
  const [jobId, setJobId] = useState<string | null>(null);
  const reset = (): void => {
    setState({ running: false, steps: [] });
    setJobId(null);
  };

  function tracker() {
    const steps: Step[] = [];
    const push = (label: string): void => {
      steps.push({ label, status: "running" });
      setState({ running: true, steps: [...steps] });
    };
    const finish = (status: StepStatus, detail?: string): void => {
      const last = steps[steps.length - 1];
      if (last) {
        last.status = status;
        if (detail !== undefined) last.detail = detail;
      }
      setState({ running: true, steps: [...steps] });
    };
    /** Update the running step's detail without finishing it (live poll progress). */
    const tick = (detail: string): void => {
      const last = steps[steps.length - 1];
      if (last && last.status === "running") {
        last.detail = detail;
        setState({ running: true, steps: [...steps] });
      }
    };
    return { steps, push, finish, tick };
  }

  /** Single-image (or cache-hit) generation: POST ids → maybe poll one job. */
  async function run(body: GenerationRequest): Promise<void> {
    const { steps, push, finish, tick } = tracker();
    try {
      push("Generate");
      const gen = await requestGeneration(service, body);
      if (gen.httpStatus >= 400) {
        finish("error", `HTTP ${gen.httpStatus}: ${gen.error ?? "request failed"}`);
        setState({ running: false, steps: [...steps], error: gen.error ?? `HTTP ${gen.httpStatus}` });
        return;
      }
      if (gen.status === "complete" || gen.status === "noop") {
        finish("done", gen.status);
        setState({ running: false, steps: [...steps], result: { status: gen.status, url: gen.url } });
        return;
      }
      if (gen.status === "rejected") {
        finish("done", "rejected");
        setState({ running: false, steps: [...steps], result: { status: "rejected" } });
        return;
      }

      finish("done", `accepted · job ${gen.jobId?.slice(0, 8)}…`);
      push("Awaiting worker");
      if (!gen.jobId) throw new Error("accepted but no job_id returned");
      setJobId(gen.jobId);
      const job = await pollToFinal(service, gen.jobId, (attempt, s) => tick(`poll ${attempt}/${POLL_MAX_TRIES} · ${s}s`));
      finish(job.status === "failed" ? "error" : job.status === "timeout" ? "stopped" : "done", job.status === "timeout" ? `still running after ${pollWindowLabel()}` : job.status);
      setState({ running: false, steps: [...steps], result: { status: job.status, url: job.url } });
    } catch (e) {
      const msg = (e as Error).message;
      finish("error", msg);
      setState({ running: false, steps: [...steps], error: msg });
    }
  }

  /** all_images fan-out: POST ids → poll each per-photo job. */
  async function runBatch(body: GenerationRequest): Promise<void> {
    const { steps, push, finish, tick } = tracker();
    try {
      push("Generate (all_images)");
      const gen = await requestGeneration(service, body);
      if (gen.httpStatus >= 400 || !gen.results) {
        finish("error", `HTTP ${gen.httpStatus}: ${gen.error ?? "no results"}`);
        setState({ running: false, steps: [...steps], error: gen.error ?? `HTTP ${gen.httpStatus}` });
        return;
      }
      finish("done", `${gen.results.length} photos`);

      push("Awaiting workers");
      // The fan-out's jobs are independent and the workers run them concurrently, so watch them
      // concurrently too. Polling them in series made the grid wait on the slowest-so-far before
      // showing any later photo that had already landed — and with a 6-minute window per job, a
      // series of six could have run for well over half an hour. Now the wait is the slowest job,
      // not the sum, and each tile fills the moment its own job settles.
      const total = gen.results.length;
      const items: BatchItem[] = gen.results.map((r) => ({ ringImageId: r.ring_image_id, sourceUrl: "", status: r.status, url: r.url, error: r.error }));
      let settled = items.filter((i) => i.status !== "accepted").length;
      tick(`${settled}/${total} settled`);
      setState({ running: true, steps: [...steps], batch: [...items] });

      await Promise.all(
        gen.results.map(async (r, n) => {
          if (r.status !== "accepted" || !r.job_id) return;
          const job = await pollToFinal(service, r.job_id);
          items[n] = { ringImageId: r.ring_image_id, sourceUrl: "", status: job.status, url: job.url };
          settled += 1;
          tick(`${settled}/${total} settled`);
          setState({ running: true, steps: [...steps], batch: [...items] });
        }),
      );
      finish("done", `${total} photos`);
      setState({ running: false, steps: [...steps], batch: items });
    } catch (e) {
      const msg = (e as Error).message;
      finish("error", msg);
      setState({ running: false, steps: [...steps], error: msg });
    }
  }

  return { state, jobId, run, runBatch, reset };
}

const KEEP_METAL = ""; // sentinel for "don't swap the metal"

/** Header status chip derived from the run state — one glance tells you where the pipeline is. */
function runStatus(run: RunState): { cls: string; label: string } {
  if (run.running) return { cls: "running", label: "generating…" };
  if (run.error) return { cls: "error", label: "error" };
  if (run.batch) {
    const done = run.batch.filter((b) => b.status === "complete").length;
    return { cls: done === run.batch.length ? "done" : "error", label: `${done}/${run.batch.length} complete` };
  }
  if (run.result) {
    const s = run.result.status;
    if (s === "complete") return { cls: "done", label: "complete" };
    if (s === "noop") return { cls: "done", label: "no-op" };
    if (s === "timeout") return { cls: "", label: "still running" }; // stopped watching, not failed
    if (TERMINAL_BAD.has(s)) return { cls: "error", label: s };
    return { cls: "", label: s };
  }
  return { cls: "", label: "ready" };
}

/** Copyable job reference — testers cross-reference job ids in worker logs and the DB. */
function JobRef({ jobId }: { jobId: string }) {
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    void navigator.clipboard.writeText(jobId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <div className="lab-job">
      <span className="muted">job</span>
      <span className="id" title={jobId}>
        {jobId}
      </span>
      <button className="btn small-btn" type="button" onClick={copy}>
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

/** Empty plate — a loupe sketch until a specimen is chosen. */
function EmptyPlate() {
  return (
    <div className="lab-plate">
      <div className="lab-emptyplate">
        <svg width="52" height="52" viewBox="0 0 48 48" fill="none" aria-hidden="true">
          <circle cx="20" cy="20" r="13" stroke="var(--ink)" strokeWidth="1.6" opacity="0.55" />
          <circle cx="20" cy="20" r="7" stroke="var(--gold)" strokeWidth="1.2" opacity="0.7" />
          <path d="M30 30l10 10" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" opacity="0.55" />
        </svg>
        <span className="t">No specimen on the plate</span>
        <span className="d">Open the catalogue at station 01 to pick a ring.</span>
      </div>
      <span className="tick tl" />
      <span className="tick tr" />
      <span className="tick bl" />
      <span className="tick br" />
    </div>
  );
}

/** Station 01 once a ring is chosen: who is on the plate, and the facts that shape the swap.
 *  The exhaustive sheet lives in the catalogue, where there's room for it. */
function SpecimenStrip({ ring, onChange }: { ring: RingDetail; onChange: () => void }) {
  const thumb = ring.images.find((i) => i.isDefault)?.url ?? ring.images[0]?.url ?? null;
  const stone = ring.currentStone;
  const colour = stone ? colourBranch(stone) : null;
  return (
    <div className="lab-strip">
      {thumb ? <img className="lab-stripshot" src={thumb} alt="" /> : <span className="lab-stripshot empty">—</span>}
      <div className="lab-stripid">
        <span className="sku">{ring.sku}</span>
        <span className="nm">{ring.name}</span>
      </div>
      <div className="lab-stripfacts">
        {ring.productType && <span className="tag mono">{ring.productType}</span>}
        {ring.metals.map((m) => {
          const d = metalDisplay(m.metal);
          return (
            <span key={m.metal} className={cx("tag", m.metal === ring.defaultMetal && "is-default")} title={m.metal}>
              <span className={cx("sw", "bead", d.swatchClass)} />
              {d.family} {d.karat}
            </span>
          );
        })}
        {stone && (
          <span className="tag">
            {stone.type}
            {isRecorded(stone.shape) ? ` · ${stone.shape}` : ""}
            {typeof stone.carat === "number" ? ` · ${stone.carat}ct` : ""}
            {isRecorded(stone.clarity) ? ` · ${stone.clarity}` : ""}
            {colour ? ` · ${colour.value}` : ""}
          </span>
        )}
        {typeof ring.price === "number" && <span className="tag">£{ring.price.toLocaleString("en-GB")}</span>}
        <span className="tag">{ring.images.length} photos</span>
      </div>
      <button className="btn small-btn lab-stripchange" type="button" onClick={onChange}>
        Change
      </button>
    </div>
  );
}

export function TestBench() {
  const { service } = useAuth();
  const queryClient = useQueryClient();
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [targetMetal, setTargetMetal] = useState<string>(KEEP_METAL);
  const [stone, setStone] = useState<GemstoneSummary | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string>("");
  const [batchMode, setBatchMode] = useState(false);
  const [catalogue, setCatalogue] = useState<SpecimenKind | null>(null);
  // Filter state lives here, not in the drawer, so closing and reopening the catalogue returns a
  // tester to the query they had built — the drawer itself is remounted each time.
  const [wire, setWire] = useState<Record<SpecimenKind, WireFilters>>({ ring: emptyWire("ring"), stone: emptyWire("stone") });
  const [sift, setSift] = useState<Record<SpecimenKind, SiftFilters>>({ ring: emptySift(), stone: emptySift() });
  const location = useLocation();
  // Router prefill (from History → "Open in Test Bench"), captured once. Applied after the ring
  // loads, then cleared, so later ring switches behave normally.
  const prefillRef = useRef<BenchPrefill | null>((location.state as { prefill?: BenchPrefill } | null)?.prefill ?? null);
  const { state: run, jobId, run: doRun, runBatch: doRunBatch, reset } = useBenchRun(service);

  const ringQuery = useQuery({
    queryKey: ["bench-ring", selectedSku],
    queryFn: () => getRing(service, selectedSku as string),
    enabled: selectedSku !== null,
  });
  const ring = ringQuery.data;

  useEffect(() => {
    // A router prefill names a ring by SKU; fetch it as usual. Otherwise the tester starts at the
    // catalogue, which the empty plate points them to.
    const p = prefillRef.current;
    if (p) setSelectedSku(p.ring_sku);
  }, []);

  useEffect(() => {
    if (ring) {
      setTargetMetal(KEEP_METAL);
      setStone(null);
      // Default to the ring's default image (or the first); "" lets the API fall back too.
      const def = ring.images.find((i) => i.isDefault) ?? ring.images[0];
      setSelectedImageId(def?.providerId ?? "");
      setBatchMode(false);
      reset();

      // Apply a one-shot router prefill (History → Open in Test Bench), then clear the ref so
      // subsequent ring switches keep the plain defaults above.
      const p = prefillRef.current;
      if (p && ring.sku === p.ring_sku) {
        if (p.target_metal) setTargetMetal(p.target_metal);
        if (p.stone) setStone(p.stone);
        if (p.ring_image_id) {
          // p.ring_image_id is a PRODUCTS image id — match on productsImageId, key UI state by providerId.
          const match = ring.images.find((i) => i.productsImageId === p.ring_image_id);
          if (match) setSelectedImageId(match.providerId);
        }
        prefillRef.current = null;
      }
    }
  }, [ring]);

  /** The catalogue row is already a complete RingDetail — products-api returns the same shape from
   *  the list and detail routes — so seed the cache and skip the round-trip. */
  const useRing = (r: RingDetail): void => {
    queryClient.setQueryData(["bench-ring", r.sku], r);
    setSelectedSku(r.sku);
    setCatalogue(null);
  };

  const useStone = (g: GemstoneSummary): void => {
    setStone(g);
    setCatalogue(null);
  };

  const selectedImage = ring?.images.find((i) => i.providerId === selectedImageId);
  const sourceUrl = selectedImage?.url ?? ring?.images[0]?.url ?? "";

  // The id-based request body. Build it once; it doubles as the dry-run preview.
  const requestBody = useMemo<GenerationRequest | null>(() => {
    if (!ring) return null;
    const metalChange = targetMetal !== KEEP_METAL;
    if (!metalChange && !stone) return null; // need at least one of stone / metal
    const body: GenerationRequest = { ring_sku: ring.sku };
    if (stone) body.stone_sku = stone.id;
    if (metalChange) body.target_metal = targetMetal;
    if (batchMode) body.all_images = true;
    else if (selectedImageId) {
      // `selectedImageId` is a Cloudinary providerId (UI key); the wire `ring_image_id` must be the
      // matching products-api image row id. Omit it when unmappable so the API defaults to the
      // product's default image (matching today's actual behaviour).
      const sel = ring.images.find((i) => i.providerId === selectedImageId);
      if (sel?.productsImageId) body.ring_image_id = sel.productsImageId;
    }
    return body;
  }, [ring, targetMetal, stone, batchMode, selectedImageId]);

  const curlString = requestBody ? toCurl(GENERATIONS_URL, requestBody) : null;
  const outOfAllowed = !!ring && targetMetal !== KEEP_METAL && !ring.allowedMetals.includes(targetMetal);
  const canGenerate = !!requestBody && !run.running;

  // Mirror of the API's operation inference, so the tester sees what will run before running it.
  const metalChange = targetMetal !== KEEP_METAL;
  const inferredOp = stone && metalChange ? "METAL_AND_STONE_SWAP" : stone ? "STONE_SWAP" : metalChange ? "METAL_SWAP" : null;
  const likelyNoop = inferredOp === "METAL_SWAP" && !!ring && targetMetal === ring.defaultMetal;

  const onGenerate = (): void => {
    if (!requestBody) return;
    if (requestBody.all_images) void doRunBatch(requestBody);
    else void doRun(requestBody);
  };

  // ── plate content: source → scanning → before/after → batch grid ──
  const displayResultUrl = run.result?.url ? (toDisplayUrl(run.result.url) ?? run.result.url) : undefined;
  const batchItems = run.batch?.map((b) => (b.url ? { ...b, url: toDisplayUrl(b.url) ?? b.url } : b));
  const status = runStatus(run);

  const plate = (() => {
    if (!ring) return <EmptyPlate />;
    if (batchItems) return <BatchGrid items={batchItems} />;
    if (displayResultUrl) return <BeforeAfter before={sourceUrl} after={displayResultUrl} />;
    return (
      <div className={cx("lab-plate", run.running && "scanning")}>
        {sourceUrl ? <img className="hero" src={sourceUrl} alt={ring.sku} /> : <span className="no-image">no image</span>}
        <span className="tick tl" />
        <span className="tick tr" />
        <span className="tick bl" />
        <span className="tick br" />
        {run.running && <span className="lab-scanbadge">generating…</span>}
      </div>
    );
  })();

  const captionStone = stone?.stone ?? ring?.currentStone ?? null;
  const metalTag = metalChange ? metalDisplay(targetMetal) : null;
  const badResult = run.result && TERMINAL_BAD.has(run.result.status);
  const stoneColour = stone ? colourBranch(stone.stone) : null;

  return (
    <main className="lab">
      <div className="lab-head">
        <div>
          <p className="lab-eyebrow">Aardvark Jewellery — Image Lab</p>
          <h1 className="lab-title">Test Bench</h1>
          <p className="lab-sub">Compose a swap from the live catalogue, dry-run the exact request, and compare the generated ring against the original.</p>
        </div>
        <div className="lab-head-right">
          <span className={cx("pill", status.cls)}>
            <span className="dot" />
            {status.label}
          </span>
          <button className="btn ghost small-btn" onClick={reset} disabled={run.running}>
            Reset
          </button>
        </div>
      </div>

      <div className="lab-cols">
        {/* 01 specimen + 02 plate — the stage */}
        <section className="lab-col lab-stage">
          <div className="lab-station">
            <span className="no">01</span>
            <span>Specimen</span>
            <span className="hint">ring_sku</span>
          </div>
          {ring ? (
            <SpecimenStrip ring={ring} onChange={() => setCatalogue("ring")} />
          ) : (
            <button className="btn lab-open" type="button" onClick={() => setCatalogue("ring")} disabled={ringQuery.isLoading}>
              {ringQuery.isLoading ? "Loading ring…" : "Open the ring catalogue"}
            </button>
          )}
          {ringQuery.isError && <p className="error small">products-api error: {(ringQuery.error as Error).message}</p>}

          <div className="lab-station lab-station-2">
            <span className="no">02</span>
            <span>{displayResultUrl ? "Comparison" : batchItems ? "Results" : "Plate"}</span>
            <span className="hint">{displayResultUrl ? "drag to reveal" : "the specimen"}</span>
          </div>
          {/* The plate and the telemetry sit side by side: a square plate in a wide column leaves
              slack either side, and the dry-run payload is too central to this bench to live below
              the fold. Under 92rem the telemetry drops back beneath the plate. */}
          <div className="lab-stagegrid">
            <div>
              {plate}
              {ring && (
                <>
                  <div className="lab-caption">
                {captionStone && (
                  <span className="tag">
                    {captionStone.type ?? "stone"}
                    {isRecorded(captionStone.shape) ? ` · ${captionStone.shape}` : ""}
                    {typeof captionStone.carat === "number" ? ` · ${captionStone.carat}ct` : ""}
                  </span>
                )}
                    {metalTag ? (
                      <span className="tag">
                        <span className={cx("sw", "bead", metalTag.swatchClass)} />
                        → {metalTag.family} {metalTag.karat}
                      </span>
                    ) : (
                      ring.defaultMetal && <span className="tag">keeps {metalDisplay(ring.defaultMetal).family.toLowerCase()}</span>
                    )}
                  </div>
                  {run.error && <div className="lab-statusline error">✕ {run.error}</div>}
                  {!run.error && badResult && (
                    <div className="lab-statusline error">
                      ✕ {run.result!.status === "failed" ? "job failed — check the worker logs" : `terminal status · ${run.result!.status}`}
                    </div>
                  )}
                  {!run.error && run.result?.status === "timeout" && (
                    <div className="lab-statusline warn">
                      ⋯ Still running after {pollWindowLabel()} — the bench stopped watching, the job didn’t stop. It will finish, cache, and show up in History.
                    </div>
                  )}
                  {!run.error && run.result && !badResult && run.result.status !== "timeout" && !run.result.url && (
                    <div className="lab-statusline">terminal status · {run.result.status}</div>
                  )}
                </>
              )}
            </div>

            {ring && (
              <div className="lab-telemetry">
                <div>
                  <div className="lab-divlabel">dry run</div>
                  <PayloadPreview payload={requestBody} curl={curlString} imageUrl={batchMode ? "(all images)" : sourceUrl} />
                </div>
                <div>
                  {run.steps.length > 0 && (
                    <>
                      <div className="lab-divlabel">process</div>
                      <ol className="lab-steps">
                        {run.steps.map((s, i) => (
                          <li key={i} className={cx("lab-step", s.status)}>
                            <span className="ic">{s.status === "running" ? <span className="lab-spin" /> : s.status === "done" ? "✓" : s.status === "stopped" ? "⋯" : "✕"}</span>
                            <span className="lbl">{s.label}</span>
                            {s.detail && <span className="dt">{s.detail}</span>}
                          </li>
                        ))}
                      </ol>
                      {jobId && <JobRef jobId={jobId} />}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* 03 — recipe */}
        <section className="lab-col">
          <div className="lab-station">
            <span className="no">03</span>
            <span>Recipe</span>
            <span className="hint">the swap</span>
          </div>

          {!ring && <p className="lab-empty">Pick a specimen first.</p>}

          {ring && (
            <>
              <div className="lab-field">
                <div className="lab-fieldlabel">
                  <span className="k">Target metal</span>
                  <span className="wire">target_metal</span>
                </div>
                <div className="lab-metals">
                  <button
                    type="button"
                    className={cx("lab-swatch", "keep", targetMetal === KEEP_METAL && "selected")}
                    onClick={() => setTargetMetal(KEEP_METAL)}
                    title="Keep the ring's current metal"
                  >
                    <span className="bead" />
                    <span className="fam">Keep</span>
                    <span className="kt">—</span>
                  </button>
                  {KNOWN_METALS.map((m) => {
                    const d = metalDisplay(m);
                    const offered = ring.allowedMetals.includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        className={cx("lab-swatch", offered && "offered", targetMetal === m && "selected")}
                        onClick={() => setTargetMetal(m)}
                        title={offered ? `${m} — offered by this ring` : `${m} — out-of-catalogue swap`}
                      >
                        <span className={cx("bead", d.swatchClass)} />
                        <span className="fam">{d.family}</span>
                        <span className="kt">{d.karat}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="lab-legend">
                  <span className="d" /> offered by this ring · others probe an out-of-catalogue swap
                </div>
                {outOfAllowed && <p className="lab-warnline">⚠ {targetMetal} isn’t one of this ring’s offered metals.</p>}
              </div>

              <div className="lab-field">
                <div className="lab-fieldlabel">
                  <span className="k">Loose stone</span>
                  <span className="wire">stone_sku</span>
                </div>
                {stone ? (
                  <div className="lab-stonepick">
                    {stone.thumbnailUrl ? <img src={stone.thumbnailUrl} alt="" /> : <span className="thumb empty">—</span>}
                    <span className="meta">
                      <span className="t">
                        {typeof stone.stone.carat === "number" ? `${stone.stone.carat}ct ` : ""}
                        {stone.stone.type}
                        {isRecorded(stone.stone.shape) ? ` · ${stone.stone.shape}` : ""}
                      </span>
                      <span className="d">
                        {[isRecorded(stone.stone.clarity) ? stone.stone.clarity : null, stoneColour?.value, stone.stone.source].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="acts">
                      <button className="btn small-btn" type="button" onClick={() => setCatalogue("stone")}>
                        Change
                      </button>
                      <button className="btn ghost small-btn" type="button" onClick={() => setStone(null)}>
                        Clear
                      </button>
                    </span>
                  </div>
                ) : (
                  <button className="btn lab-open" type="button" onClick={() => setCatalogue("stone")}>
                    Open the stone catalogue
                  </button>
                )}
              </div>

              {ring.images.length > 1 && (
                <div className="lab-field">
                  <div className="lab-fieldlabel">
                    <span className="k">Source photo</span>
                    <span className="opt">{ring.images.length} available</span>
                  </div>
                  {!batchMode && (
                    <div className="lab-film">
                      {ring.images.map((im) => (
                        <button key={im.providerId} className={cx(im.providerId === selectedImageId && "selected")} onClick={() => setSelectedImageId(im.providerId)}>
                          <img src={im.url} alt="" />
                        </button>
                      ))}
                    </div>
                  )}
                  <label className="lab-check">
                    <input type="checkbox" checked={batchMode} onChange={(e) => setBatchMode(e.target.checked)} />
                    Run on all {ring.images.length} photos <span className="mono small muted">all_images</span>
                  </label>
                </div>
              )}

              {ring.warnings.map((w) => (
                <p key={w} className="lab-warnline">
                  ⚠ {w}
                </p>
              ))}

              <div className={cx("lab-op", !inferredOp && "none")}>
                {inferredOp ? (
                  <>
                    <span>operation</span>
                    <span className="arrow">→</span>
                    <span className="opname">{inferredOp}</span>
                    {likelyNoop && <span className="noophint">= default metal · likely no-op</span>}
                  </>
                ) : (
                  <span>operation → pick a metal and/or a stone</span>
                )}
              </div>

              <button className="btn primary lab-generate" disabled={!canGenerate} onClick={onGenerate}>
                {run.running ? "Running…" : "Generate"}
              </button>
            </>
          )}
        </section>
      </div>

      {catalogue && (
        <Catalogue
          kind={catalogue}
          service={service}
          wire={wire[catalogue]}
          onWire={(w) => setWire((s) => ({ ...s, [catalogue]: w }))}
          sift={sift[catalogue]}
          onSift={(s) => setSift((prev) => ({ ...prev, [catalogue]: s }))}
          onClose={() => setCatalogue(null)}
          onUseRing={useRing}
          onUseStone={useStone}
        />
      )}
    </main>
  );
}
